import { useBlocker } from '@tanstack/react-router';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { IxcRegistrationV1ProfileType, IxcRegistrationV1ProfileTypeOneOfValue } from '@interxfi/types/generated/api';
import type { IndividualProfileFormValues } from './individual/individual-profile-form';
import type { OrganizationProfileFormValues } from './organization/organization-profile-form';

export type InProgressFormValues =
  | {
      programId: string;
      profileId?: string;
      individual: IndividualProfileFormValues;
    }
  | {
      programId: string;
      profileId?: string;
      organization: OrganizationProfileFormValues;
    };

export type PendingProfileValues = { programId: string; spec: IxcRegistrationV1ProfileType };

interface ProfileFormSubmissionContext {
  pendingProfile: PendingProfileValues | undefined;
  setPendingProfile: (pendingProfile: PendingProfileValues | undefined) => void;
  inProgressProfile: InProgressFormValues | undefined;
  setInProgressProfile: (inProgressProfile: InProgressFormValues) => void;
  consumeInProgressProfile: (kind: IxcRegistrationV1ProfileTypeOneOfValue | undefined) => InProgressFormValues | undefined;
  reset: VoidFunction;
}

export const useProfileFormSubmissionContext = create<ProfileFormSubmissionContext>((set, get) => ({
  pendingProfile: undefined,
  setPendingProfile: (pendingProfile) => set({ pendingProfile }),
  inProgressProfile: undefined,
  setInProgressProfile: (inProgressProfile) => set({ inProgressProfile }),
  consumeInProgressProfile: (kind: IxcRegistrationV1ProfileTypeOneOfValue | undefined) => {
    const { inProgressProfile } = get();
    set({ inProgressProfile: undefined });

    if (inProgressProfile && (!kind || Object.hasOwn(inProgressProfile, kind))) {
      return inProgressProfile;
    }

    return undefined;
  },
  reset: () => set({ pendingProfile: undefined, inProgressProfile: undefined }),
}));

export function useSetPendingProfile() {
  return useProfileFormSubmissionContext(useShallow((state) => state.setPendingProfile));
}

export function useSetInProgressProfile() {
  return useProfileFormSubmissionContext(useShallow((state) => state.setInProgressProfile));
}

export function useResetProfileFormSubmissionContext() {
  return useProfileFormSubmissionContext(useShallow((state) => state.reset));
}

export function useResetProfileFormSubmissionContextOnNavigateAway(routePrefix: string) {
  const reset = useResetProfileFormSubmissionContext();

  // We are not using this to actually block navigation, just to clear the profile submission context
  useBlocker({
    enableBeforeUnload: false,
    shouldBlockFn: ({ next }) => {
      if (!next.pathname.startsWith(routePrefix)) {
        reset();
      }

      return false;
    },
  });
}
